import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import config from '@iotv/config';
import { DeviceConfig, getZombieInstanceFromPrimaryKey, } from '@iotv/datamodel-core';
import { DeviceTransactionStatus, DeviceTypes, isNode, isVOBofType, isValidBusinessObject } from '@iotv/iotv-v3-types';
import { Container, Grid, Paper, Tab, Tabs } from '@mui/material';
import React, { useEffect } from 'react';
import AppDao from '../../data/AppDao';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { AppLocalStorage } from '../../data/LocalStorage/AppLocalStorage';
import { getExact, getParents } from '../../data/daoFunctions/daoFunctions';
import { useDocumentClientQuery } from '../../hooks/useDocumentClientQueryV1';
import { useMQTTChannelMessages } from '../../hooks/useMQTTChannelMessages';
import { useSelectedObjects } from '../../hooks/useSelectedObjects';
import { useTabHandler } from '../../hooks/useTabHandler';
import { SlaveTab } from '../factories/SlaveTabV2';
import { NavSections } from '../navigation/NavSections';
import { getDeviceTransactionQueryParams, getNetworkQueryResults } from './queries';
import { ConfigurationTab, MonitorTab as DefaultMonitorTab, HistoryTab, SelectionTab } from './tabs';
const { REACT_APP_PROJECT_CODE: fullProjectCode } = process.env;
const debug = process.env.REACT_APP_DEBUG && false;
const suppressActualSending = debug && false;
const componentClassName = 'DiagnosticView';
const projectCode = fullProjectCode?.substring(0, 4) ?? 'i301';
const tabsMap = {
    [DeviceTypes.GenericSensor]: {
        CalibrationTab: () => null,
        DiagnosticModeTab: () => null,
        ConfigurationTab: () => null,
        // ConfigurationTab: DefaultConfigurationTab,
        MonitorTab: DefaultMonitorTab,
    },
};
debug && console.log(`DeviceControlView has projectCode ${projectCode} from ${fullProjectCode} `);
export const openHandleSendTransactionButtonClick = async (desiredPayload, user, selectedDevices) => {
    if (desiredPayload && user) {
        const deviceControlTransactions = {
            devices: selectedDevices,
            desiredPayload: {},
            requestInitiator: user,
        };
        deviceControlTransactions.desiredPayload = desiredPayload;
        if (suppressActualSending) {
            console.log('INFO We would send', deviceControlTransactions);
        }
        else {
            const putRes = AppDao.submitDeviceTransaction(deviceControlTransactions);
            console.log('INFO We ARE sending', deviceControlTransactions, putRes);
            putRes.then((res) => console.log('INFO We ARE sending what is in res', res));
            /**
             * TODO
             *   setReceivedDeviceControlTransactions( [ newTransaction, ...receivedDeviceControlTransactions,])
             */
            // setReceivedDeviceControlTransactions( [ newTransaction, ...receivedDeviceControlTransactions,])
        }
    }
};
/** TODO - this should be replaced when NetworkData is changed to send device only decoded payload */
const getThingForDevice = async (device, setThing) => {
    const parentsAndEdges = await getParents(device, 'Horse');
    console.log('WARN: getThingForDevice is using Horse!!', parentsAndEdges);
    const parent = parentsAndEdges.data?.Items?.find((item) => isNode(item));
    if (isValidBusinessObject(parent)) {
        setThing(parent);
    }
};
const DiagnosticView = ({ user, contextCustomer, userFunctions, ...others }) => {
    const classes = {};
    const common = { ...others, contextCustomer, user };
    const [currentDeviceType, setCurrentDeviceType] = React.useState(undefined);
    const projectTabsMap = (currentDeviceType && tabsMap[currentDeviceType]) ?? tabsMap[DeviceTypes.GenericSensor];
    const { CalibrationTab, DiagnosticModeTab, /*ConfigurationTab,*/ MonitorTab } = projectTabsMap;
    const [deviceControlMessages, setDeviceControlMessages] = React.useState([]);
    const { state: [[selectedDevices, setSelectedDevices]], } = useSelectedObjects();
    const selectedDeviceSk = selectedDevices[0]?.sk;
    useEffect(() => {
        const selectedDevice = selectedDevices[0];
        if (selectedDevice) {
            setCurrentDeviceType(selectedDevice.deviceType);
        }
    }, [selectedDeviceSk]);
    useEffect(() => {
        const loadDevice = async (deviceSk) => {
            const { err, data } = await ConsumerDao.getExact(getZombieInstanceFromPrimaryKey(deviceSk));
            if (err) {
                console.log('Err loading device', err.message);
            }
            if (data?.Items && data.Items.length > 0) {
                setSelectedDevices([data.Items[0]]);
            }
            debug && console.log('got loading device res', data);
        };
        if (selectedDevices.length === 0) {
            const lastSelectedDeviceSk = AppLocalStorage.get(NavSections.DIAGNOSTIC, 'lastSelectedDeviceSk');
            if (lastSelectedDeviceSk) {
                loadDevice(lastSelectedDeviceSk);
            }
        }
    }, []);
    /** subscribe to device transaction topic */
    const deviceTransactionMqttTopic = selectedDeviceSk ? config.aws.mqtt.deviceControlTransactionChannel.replace('[deviceSk]', selectedDeviceSk) : undefined;
    const deviceTransactionObjectFilter = (x) => isVOBofType('DeviceControlTransaction', x);
    const { setterGetters: [receivedDeviceControlTransactions, setReceivedDeviceControlTransactions], } = useMQTTChannelMessages({
        user,
        topic: deviceTransactionMqttTopic,
        objectFilter: deviceTransactionObjectFilter,
    });
    const { querySetterGetters: [[currentDeviceTransactions], [_limit, setTransactionsLimit], [lastEvaluatedTransactionKeys], [_exclusiveStartKey, setTransactionsExclusiveStartKey],], } = useDocumentClientQuery(getNetworkQueryResults, getDeviceTransactionQueryParams(selectedDeviceSk), [
        selectedDeviceSk, // as you guessed these get passed to the dependency array in the hook
    ]);
    const [thing, setThing] = React.useState(undefined);
    /** subscribe to thing messages topic */
    const thingMessagesMqttTopic = thing ? config.aws.mqtt.appDeviceMessageConfirmedTopice.replace('thingId', thing.id) : undefined;
    const { setterGetters: [receivedThingMessages, setReceivedThingMessages], } = useMQTTChannelMessages({
        user,
        topic: thingMessagesMqttTopic,
        objectFilter: (x) => !!x,
    });
    const [deviceConfig, setDeviceConfig] = React.useState(undefined);
    const [status, setStatus] = React.useState(null);
    const handleSendTransactionButtonClick = async (desiredPayload) => {
        openHandleSendTransactionButtonClick(desiredPayload, user, selectedDevices);
    };
    useEffect(() => {
        debug && console.log('receivedDeviceControlTransactions', receivedDeviceControlTransactions.length);
        if (currentDeviceTransactions[0]?.pk === selectedDeviceSk) {
            setStatus(currentDeviceTransactions[0]?.status);
        }
        else {
            setStatus(DeviceTransactionStatus.USER_REQUESTED);
        }
    }, [receivedDeviceControlTransactions.length, currentDeviceTransactions, selectedDeviceSk]);
    useEffect(() => {
        debug && console.log('DeviceControlView using effect on selectedDeviceSk', selectedDeviceSk);
        if (selectedDeviceSk) {
            getThingForDevice(getZombieInstanceFromPrimaryKey(selectedDeviceSk), setThing);
        }
    }, [selectedDeviceSk]);
    const getDeviceConfig = async (selectedDeviceSk) => {
        const deviceConfigZombie = DeviceConfig.createForDevice(getZombieInstanceFromPrimaryKey(selectedDeviceSk));
        const getDeviceConfigRes = await getExact(deviceConfigZombie);
        if (getDeviceConfigRes.err) {
            console.log(`DeviceControlView getDeviceConfig error:`, getDeviceConfigRes.err);
        }
        else if (getDeviceConfigRes.data?.Items?.[0]) {
            debug && console.log(`DeviceControView getDeviceConfig loaded deviceConfig for ${selectedDeviceSk}:`, getDeviceConfigRes.data.Items[0]);
            setDeviceConfig(new DeviceConfig(getDeviceConfigRes.data.Items[0]));
        }
        else {
            console.log(`DeviceControlView could not get device config from zombie:`, { deviceConfigZombie });
        }
    };
    useEffect(() => {
        if (selectedDeviceSk) {
            getDeviceConfig(selectedDeviceSk);
            AppLocalStorage.set(NavSections.DIAGNOSTIC, 'lastSelectedDeviceSk', selectedDeviceSk);
        }
    }, [selectedDeviceSk]);
    const currentDeviceTransaction = receivedDeviceControlTransactions[0];
    const tabsToDisplay = {
        Select: () => (_jsx(SelectionTab, { value: tabValue,
            index: 0,
            classes,
            selectedDevices,
            setSelectedDevices,
            setReceivedDeviceControlTransactions,
            handleSendTransactionButtonClick,
            userFunctions,
            ...common }, `tab_a`)),
        DownLinks: () => (_jsx(HistoryTab, { value: tabValue, index: 1, classes, selectedDevices, currentDeviceTransactions, userFunctions, ...common }, `tab_b`)),
        Configure: () => (_jsx(ConfigurationTab, { value: tabValue,
            index: 2,
            classes,
            deviceConfig,
            currentDeviceTransaction,
            receivedNetworkObjects: receivedDeviceControlTransactions,
            setReceivedDeviceControlTransactions,
            handleSendTransactionButtonClick,
            userFunctions,
            selectedDevices,
            ...common }, `tab_c`)),
        Calibrate: () => (_jsx(CalibrationTab, { value: tabValue,
            index: 3,
            classes,
            deviceConfig,
            currentDeviceTransaction,
            deviceMessages: receivedThingMessages,
            selectedDevices,
            userFunctions,
            handleSendTransactionButtonClick,
            setReceivedDeviceControlTransactions,
            receivedDeviceControlTransactions,
            ...common }, `tab_e`)),
        DiagnosticMode: () => (_jsx(DiagnosticModeTab, { value: tabValue,
            index: 4,
            classes,
            deviceMessages: receivedThingMessages,
            currentDeviceTransaction,
            selectedDevices,
            userFunctions,
            handleSendTransactionButtonClick,
            setReceivedDeviceControlTransactions,
            ...common }, `tab_f`)),
        Monitor: () => (_jsx(MonitorTab, { value: tabValue,
            index: 5,
            classes,
            deviceConfig,
            thingMessages: receivedThingMessages,
            deviceControlMessages,
            currentDeviceTransaction,
            selectedDevices,
            userFunctions,
            ...common }, `tab_d`)),
    };
    const { state: [[tabValue, setTabValue]], handlers: { handleTabChange }, } = useTabHandler(tabsToDisplay);
    return (_jsx(Container, { className: componentClassName, style: { maxWidth: 'unset' }, children: _jsxs(Paper, { className: classes.paper, variant: "outlined", children: [_jsx(Grid, { className: classes.root, container: true, spacing: 3, children: _jsxs(Grid, { size: 12, children: [_jsx(Tabs, { value: tabValue, onChange: handleTabChange, children: Object.keys(tabsToDisplay).map((tabName, i) => (_jsx(Tab, { label: tabName, disabled: i > 0 && selectedDevices.length === 0 }, `tab_${i}`))) }), Object.entries(tabsToDisplay)
                                .filter(([k, c]) => c !== null)
                                .map(([k, component], index) => (_jsx(SlaveTab, { orderedComponents: [component], index, value: tabValue }, `tab${index}`)))] }) }), [2].includes(tabValue) && (_jsx(Grid, { size: 12 }, "status"))] }) }));
};
export default DiagnosticView;
